/**
 * Responsibility: write wireframe line vertices for the handful of shapes structures are
 * built from.
 *
 * Pure array arithmetic — no Three.js, no knowledge of what a building is. A caller supplies
 * arrays and an offset, these functions fill in positions and colours and report how far they
 * advanced. That shape avoids allocating anything per building, which matters because a dense
 * city tile emits several hundred of them.
 *
 * ## Colour along height, not along ground
 *
 * Structures interpolate their biome's gradient by height above their own base. A tower is
 * therefore dark at street level and bright at the roof, which is what makes it read as tall.
 * Colouring by ground elevation instead — the way terrain is coloured — would give every
 * building in a block the same flat shade regardless of how far up it went.
 */

/** Floats per vertex, for both positions and colours. */
const COMPONENTS_PER_VERTEX = 3;

/** Vertices per line segment. */
const VERTICES_PER_SEGMENT = 2;

/** Floats consumed by one line segment across both arrays. */
export const FLOATS_PER_SEGMENT = VERTICES_PER_SEGMENT * COMPONENTS_PER_VERTEX;

/**
 * Interpolate between two colours. Kept local rather than imported from `palette.js` because
 * that module's job is turning biome weights into colours, and this one only needs a lerp.
 */
function mix(from, to, t) {
  const amount = t < 0 ? 0 : t > 1 ? 1 : t;
  return {
    r: from.r + (to.r - from.r) * amount,
    g: from.g + (to.g - from.g) * amount,
    b: from.b + (to.b - from.b) * amount,
  };
}

/**
 * A cursor over a pair of position and colour arrays.
 *
 * Bundling the arrays with the write offset means every emitter has one thing to advance
 * rather than three, and a caller cannot accidentally advance one array and not the other.
 *
 * Structure placement works in absolute world coordinates — street grids and tree slots are anchored
 * to the world, not to a tile — but the vertices written out must be relative to the tile, for the
 * precision reasons set out in `tile.js`. The writer subtracts the origin so every emitter can go on
 * thinking in world coordinates and none of them has to remember.
 *
 * @param {Float32Array} positions
 * @param {Float32Array} colors
 * @param {number} [originX] - Subtracted from every X written.
 * @param {number} [originZ] - Subtracted from every Z written.
 * @returns {{write: Function, getSegmentCount: () => number, remainingSegments: () => number}}
 */
export function createGeometryWriter(positions, colors, originX = 0, originZ = 0) {
  if (positions.length !== colors.length) {
    throw new Error('createGeometryWriter: positions and colors must be the same length');
  }

  let offset = 0;

  return {
    /**
     * Append one line segment.
     *
     * @param {number} x1
     * @param {number} y1
     * @param {number} z1
     * @param {number} x2
     * @param {number} y2
     * @param {number} z2
     * @param {{r: number, g: number, b: number}} colorFrom
     * @param {{r: number, g: number, b: number}} colorTo
     */
    write(x1, y1, z1, x2, y2, z2, colorFrom, colorTo) {
      if (offset + FLOATS_PER_SEGMENT > positions.length) {
        /*
          Overrunning would silently drop geometry or, worse, wrap and corrupt earlier
          segments. Callers size their arrays from a segment estimate, so this firing means
          the estimate is wrong and needs fixing rather than tolerating.
        */
        throw new Error('geometryWriter: ran out of room; the segment estimate is too low');
      }

      positions[offset] = x1 - originX;
      positions[offset + 1] = y1;
      positions[offset + 2] = z1 - originZ;
      colors[offset] = colorFrom.r;
      colors[offset + 1] = colorFrom.g;
      colors[offset + 2] = colorFrom.b;

      positions[offset + 3] = x2 - originX;
      positions[offset + 4] = y2;
      positions[offset + 5] = z2 - originZ;
      colors[offset + 3] = colorTo.r;
      colors[offset + 4] = colorTo.g;
      colors[offset + 5] = colorTo.b;

      offset += FLOATS_PER_SEGMENT;
    },

    /** Segments written so far. */
    getSegmentCount() {
      return offset / FLOATS_PER_SEGMENT;
    },

    /** Segments that still fit. */
    remainingSegments() {
      return (positions.length - offset) / FLOATS_PER_SEGMENT;
    },
  };
}

/** Line segments a box occupies: four verticals, four along the roof, four along the base. */
export const SEGMENTS_PER_BOX = 12;

/**
 * Emit a rectangular tower.
 *
 * Each base corner sits at its own ground height, so a building on a slope has its footprint
 * follow the terrain rather than floating at one corner and buried at another. The roof stays
 * level — buildings are built level even when the ground is not.
 *
 * @param {object} writer - From `createGeometryWriter`.
 * @param {object} box
 * @param {number} box.centerX
 * @param {number} box.centerZ
 * @param {number} box.width - Footprint along X.
 * @param {number} box.depth - Footprint along Z.
 * @param {number} box.roofY - Absolute height of the roof.
 * @param {Array<number>} box.cornerGroundY - Ground height at each of the four base corners,
 *   in the order (-x,-z), (+x,-z), (+x,+z), (-x,+z).
 * @param {{low: object, high: object}} gradient - Colours for the base and the roof.
 * @returns {number} Segments written.
 */
export function emitBox(writer, box, gradient) {
  const halfWidth = box.width / 2;
  const halfDepth = box.depth / 2;

  // Same winding order as `cornerGroundY`, so index i is corner i all the way through.
  const corners = [
    [box.centerX - halfWidth, box.centerZ - halfDepth],
    [box.centerX + halfWidth, box.centerZ - halfDepth],
    [box.centerX + halfWidth, box.centerZ + halfDepth],
    [box.centerX - halfWidth, box.centerZ + halfDepth],
  ];

  const roofColor = gradient.high;
  const baseColor = gradient.low;

  let written = 0;

  for (let i = 0; i < corners.length; i += 1) {
    const [x, z] = corners[i];
    const next = (i + 1) % corners.length;
    const [nextX, nextZ] = corners[next];
    const groundY = box.cornerGroundY[i];
    const nextGroundY = box.cornerGroundY[next];

    // Vertical edge, dark at the bottom and bright at the roof.
    writer.write(x, groundY, z, x, box.roofY, z, baseColor, roofColor);

    // Roof edge.
    writer.write(x, box.roofY, z, nextX, box.roofY, nextZ, roofColor, roofColor);

    // Base edge, following the ground between the two corners.
    writer.write(x, groundY, z, nextX, nextGroundY, nextZ, baseColor, baseColor);

    written += 3;
  }

  return written;
}

/**
 * Emit a list of pre-computed line segments, colouring each end by its height fraction.
 *
 * The bridge between `buildingShapes.js`, which decides what shape a building is, and the typed arrays
 * everything ends up in. The shapes carry a `t` per endpoint saying how far up the building it sits, and
 * the gradient decides what that means — so the shape vocabulary stays independent of the biome.
 *
 * @param {object} writer - From `createGeometryWriter`.
 * @param {Array<{x1: number, y1: number, z1: number, x2: number, y2: number, z2: number, t1: number, t2: number}>} segments
 * @param {{low: object, high: object}} gradient
 * @returns {number} Segments written.
 */
export function emitShapeSegments(writer, segments, gradient) {
  for (const seg of segments) {
    writer.write(
      seg.x1,
      seg.y1,
      seg.z1,
      seg.x2,
      seg.y2,
      seg.z2,
      colorAtHeight(gradient, seg.t1),
      colorAtHeight(gradient, seg.t2),
    );
  }

  return segments.length;
}

/**
 * Emit a boundary line that follows the ground.
 *
 * A field edge, a hedgerow, a track. Drawn as a chain of short segments each sampling the terrain at its own
 * ends, so the line drapes over a swell instead of cutting through it or floating above it.
 *
 * The whole line is lifted slightly. A line lying exactly on the drawn surface fights the terrain wireframe
 * for the same depth and flickers at distance — the same z-fighting the occlusion masks needed a bias for.
 *
 * @param {object} writer - From `createGeometryWriter`.
 * @param {object} line
 * @param {number} line.fromX
 * @param {number} line.fromZ
 * @param {number} line.toX
 * @param {number} line.toZ
 * @param {number} line.lift - World units above the ground.
 * @param {number} line.segments - How many pieces to draw it in.
 * @param {(x: number, z: number) => number} sampleGround - The *drawn* surface.
 * @param {{low: object, high: object}} gradient
 * @param {number} maxElevation - Height treated as the top of the gradient, so colour reads consistently
 *   with the terrain underneath rather than being normalised per line.
 * @returns {number} Segments written.
 */
export function emitGroundLine(writer, line, sampleGround, gradient, maxElevation) {
  const count = Math.max(1, Math.floor(line.segments));
  const span = 1 / count;

  let previousX = line.fromX;
  let previousZ = line.fromZ;
  let previousY = sampleGround(previousX, previousZ) + line.lift;

  for (let i = 1; i <= count; i += 1) {
    const t = i * span;
    const x = line.fromX + (line.toX - line.fromX) * t;
    const z = line.fromZ + (line.toZ - line.fromZ) * t;
    const y = sampleGround(x, z) + line.lift;

    writer.write(
      previousX,
      previousY,
      previousZ,
      x,
      y,
      z,
      colorAtHeight(gradient, previousY / maxElevation),
      colorAtHeight(gradient, y / maxElevation),
    );

    previousX = x;
    previousZ = z;
    previousY = y;
  }

  return count;
}

/** Floats per segment in a tinted segment list: two endpoints, each with a position and a gradient fraction. */
export const TINTED_FLOATS_PER_SEGMENT = 8;

/**
 * Emit a flat list of segments where every vertex carries its own gradient fraction.
 *
 * ## Why a whole tile's worth of lines arrives as one flat array
 *
 * Two things in the world are *fields* of lines rather than objects standing on the ground — a water surface
 * and a vineyard's contour rows — and both produce hundreds of short segments per tile. As objects that would
 * be hundreds of allocations a tile, which costs more than the geometry does to draw. As eight floats each it
 * is one array.
 *
 * ## Why the fraction is carried rather than derived from height
 *
 * Every other emitter here colours by absolute height over `maxElevation`, which is right for anything whose
 * meaning *is* its height. These two mean something else: water is coloured by depth, so a channel has a
 * bright core fading to nothing at the bank, and a vineyard is coloured by what a line *is*, so a retaining
 * wall reads brighter than the vine rows beside it. Neither can be recovered from a height, so it is stored.
 *
 * @param {object} writer - From `createGeometryWriter`.
 * @param {Array<number>|Float32Array} data - Eight floats per segment: x, y, z, tint, then the same again.
 * @param {number} count - Segments in `data`. Passed rather than derived so a caller that has already counted
 *   cannot disagree with this function about how many there are.
 * @param {{low: object, high: object}} gradient
 * @returns {number} Segments written.
 */
export function emitTintedSegments(writer, data, count, gradient) {
  for (let i = 0; i < count * TINTED_FLOATS_PER_SEGMENT; i += TINTED_FLOATS_PER_SEGMENT) {
    writer.write(
      data[i],
      data[i + 1],
      data[i + 2],
      data[i + 4],
      data[i + 5],
      data[i + 6],
      mixTint(gradient, data[i + 3]),
      mixTint(gradient, data[i + 7]),
    );
  }

  return count;
}

/** Gradient lookup by a stored fraction rather than by height. */
function mixTint(gradient, amount) {
  const t = Math.min(1, Math.max(0, amount));

  return {
    r: gradient.low.r + (gradient.high.r - gradient.low.r) * t,
    g: gradient.low.g + (gradient.high.g - gradient.low.g) * t,
    b: gradient.low.b + (gradient.high.b - gradient.low.b) * t,
  };
}

/** Segments a raised row occupies: a crest along its length, plus an end dropping to the ground at each end. */
export function segmentsForRow(alongSegments) {
  return Math.max(1, Math.floor(alongSegments)) + 2;
}

/**
 * Emit a raised crop row: a crest ribbon following the ground, closed down to the terrain at both ends.
 *
 * ## Why raised rather than a line on the surface
 *
 * A line lying on the ground reads as paint. A ridge reads as ground that has been worked, and it is what
 * ploughed rows physically are — parallel crests with furrows between them. Dropping each end back to the
 * terrain is what gives the ribbon visible thickness where a field meets its boundary.
 *
 * It also sidesteps a depth fight: a line at exactly the surface height flickers against the terrain
 * wireframe at distance, the same z-fighting the occlusion masks needed a bias for.
 *
 * @param {object} writer - From `createGeometryWriter`.
 * @param {object} row
 * @param {number} row.fromX
 * @param {number} row.fromZ
 * @param {number} row.toX
 * @param {number} row.toZ
 * @param {number} row.height - Crest height above the ground.
 * @param {number} row.segments - Pieces along the length.
 * @param {(x: number, z: number) => number} sampleGround - The *drawn* surface.
 * @param {{low: object, high: object}} gradient
 * @param {number} maxElevation - Height treated as the top of the gradient, so colour agrees with the terrain
 *   beneath rather than being normalised per row.
 * @returns {number} Segments written.
 */
export function emitRow(writer, row, sampleGround, gradient, maxElevation) {
  const count = Math.max(1, Math.floor(row.segments));
  const colourAt = (y) => colorAtHeight(gradient, y / maxElevation);

  const startGround = sampleGround(row.fromX, row.fromZ);
  let previousX = row.fromX;
  let previousZ = row.fromZ;
  let previousY = startGround + row.height;

  // Leading end, ground up to the crest.
  writer.write(
    row.fromX,
    startGround,
    row.fromZ,
    previousX,
    previousY,
    previousZ,
    colourAt(startGround),
    colourAt(previousY),
  );

  for (let i = 1; i <= count; i += 1) {
    const t = i / count;
    const x = row.fromX + (row.toX - row.fromX) * t;
    const z = row.fromZ + (row.toZ - row.fromZ) * t;
    const y = sampleGround(x, z) + row.height;

    writer.write(previousX, previousY, previousZ, x, y, z, colourAt(previousY), colourAt(y));

    previousX = x;
    previousZ = z;
    previousY = y;
  }

  // Trailing end, crest back down to the ground.
  const endGround = sampleGround(previousX, previousZ);
  writer.write(
    previousX,
    previousY,
    previousZ,
    previousX,
    endGround,
    previousZ,
    colourAt(previousY),
    colourAt(endGround),
  );

  return segmentsForRow(count);
}

/** Segments a gable-roofed building occupies: box edges, plus a ridge and four rafters. */
export const SEGMENTS_PER_GABLE_BUILDING = 12 + 1 + 4;

/**
 * Emit a building with a pitched roof — a farmhouse or a barn.
 *
 * ## Why a gable roof matters here
 *
 * Every other building in the world is flat-topped, so a triangular roofline is instantly not-a-tower. That
 * is the whole job: rows of worked ground are ambiguous on their own, but rows next to a barn are
 * unmistakably a farm. The silhouette does the explaining.
 *
 * @param {object} writer
 * @param {object} building
 * @param {number} building.centerX
 * @param {number} building.centerZ
 * @param {number} building.width - Footprint across the ridge.
 * @param {number} building.depth - Footprint along the ridge.
 * @param {number} building.groundY
 * @param {number} building.wallHeight
 * @param {number} building.roofHeight - Ridge height above the walls.
 * @param {number} building.facing - Radians. Which way the ridge runs.
 * @param {{low: object, high: object}} gradient
 * @returns {number} Segments written.
 */
export function emitGableBuilding(writer, building, gradient) {
  const cos = Math.cos(building.facing);
  const sin = Math.sin(building.facing);

  const halfWidth = building.width / 2;
  const halfDepth = building.depth / 2;

  /** Footprint corner in world space, from offsets across and along the ridge. */
  const at = (across, along) => [
    building.centerX + across * cos - along * sin,
    building.centerZ + across * sin + along * cos,
  ];

  const corners = [
    at(-halfWidth, -halfDepth),
    at(halfWidth, -halfDepth),
    at(halfWidth, halfDepth),
    at(-halfWidth, halfDepth),
  ];

  const baseY = building.groundY;
  const eaveY = baseY + building.wallHeight;
  const ridgeY = eaveY + building.roofHeight;

  const low = colorAtHeight(gradient, 0);
  const mid = colorAtHeight(gradient, 0.6);
  const high = colorAtHeight(gradient, 1);

  let written = 0;

  // Walls: verticals, eave line, and the footprint on the ground.
  for (let i = 0; i < 4; i += 1) {
    const [x, z] = corners[i];
    const [nextX, nextZ] = corners[(i + 1) % 4];

    writer.write(x, baseY, z, x, eaveY, z, low, mid);
    writer.write(x, eaveY, z, nextX, eaveY, nextZ, mid, mid);
    writer.write(x, baseY, z, nextX, baseY, nextZ, low, low);
    written += 3;
  }

  // Ridge, running along the depth axis above the middle of the width.
  const ridgeA = at(0, -halfDepth);
  const ridgeB = at(0, halfDepth);

  writer.write(ridgeA[0], ridgeY, ridgeA[1], ridgeB[0], ridgeY, ridgeB[1], high, high);
  written += 1;

  // Four rafters from the eave corners up to the ridge ends, which is what makes the roof read as pitched.
  writer.write(corners[0][0], eaveY, corners[0][1], ridgeA[0], ridgeY, ridgeA[1], mid, high);
  writer.write(corners[1][0], eaveY, corners[1][1], ridgeA[0], ridgeY, ridgeA[1], mid, high);
  writer.write(corners[2][0], eaveY, corners[2][1], ridgeB[0], ridgeY, ridgeB[1], mid, high);
  writer.write(corners[3][0], eaveY, corners[3][1], ridgeB[0], ridgeY, ridgeB[1], mid, high);
  written += 4;

  return written;
}

/** Segments a silo occupies, given its side count: base ring, top ring, verticals, and a domed cap. */
export function segmentsForSilo(sides) {
  return sides * 4;
}

/**
 * Emit a grain silo: a narrow cylinder with a domed cap.
 *
 * Vertical and slender, so it is the first thing seen from a distance and it says "farm" before the barn is
 * even legible. Deliberately taller than the barn for that reason.
 *
 * @param {object} writer
 * @param {object} silo
 * @param {number} silo.x
 * @param {number} silo.z
 * @param {number} silo.groundY
 * @param {number} silo.radius
 * @param {number} silo.height
 * @param {number} silo.sides
 * @param {{low: object, high: object}} gradient
 * @returns {number} Segments written.
 */
export function emitSilo(writer, silo, gradient) {
  const baseY = silo.groundY;
  const topY = baseY + silo.height;
  const capY = topY + silo.radius * 0.8;

  const low = colorAtHeight(gradient, 0);
  const high = colorAtHeight(gradient, 1);

  const point = (index) => {
    const angle = (index / silo.sides) * Math.PI * 2;
    return [silo.x + Math.cos(angle) * silo.radius, silo.z + Math.sin(angle) * silo.radius];
  };

  let written = 0;

  for (let i = 0; i < silo.sides; i += 1) {
    const [x, z] = point(i);
    const [nextX, nextZ] = point(i + 1);

    writer.write(x, baseY, z, x, topY, z, low, high);
    writer.write(x, baseY, z, nextX, baseY, nextZ, low, low);
    writer.write(x, topY, z, nextX, topY, nextZ, high, high);
    writer.write(x, topY, z, silo.x, capY, silo.z, high, high);
    written += 4;
  }

  return written;
}

/** Line segments one accent ring occupies: one per side of the footprint. */
export const SEGMENTS_PER_RING = 4;

/**
 * Emit a horizontal ring around a tower at a given height — a "lit floor".
 *
 * These are what give a tower a sense of scale. A bare wireframe box has no cue for how big
 * it is; a row of lit floors does.
 *
 * @param {object} writer
 * @param {object} ring
 * @param {number} ring.centerX
 * @param {number} ring.centerZ
 * @param {number} ring.width
 * @param {number} ring.depth
 * @param {number} ring.y - Absolute height of the ring.
 * @param {{r: number, g: number, b: number}} color
 * @returns {number} Segments written.
 */
export function emitRing(writer, ring, color) {
  const halfWidth = ring.width / 2;
  const halfDepth = ring.depth / 2;

  const corners = [
    [ring.centerX - halfWidth, ring.centerZ - halfDepth],
    [ring.centerX + halfWidth, ring.centerZ - halfDepth],
    [ring.centerX + halfWidth, ring.centerZ + halfDepth],
    [ring.centerX - halfWidth, ring.centerZ + halfDepth],
  ];

  for (let i = 0; i < corners.length; i += 1) {
    const [x, z] = corners[i];
    const [nextX, nextZ] = corners[(i + 1) % corners.length];
    writer.write(x, ring.y, z, nextX, ring.y, nextZ, color, color);
  }

  return corners.length;
}

/** Segments an antenna occupies. */
export const SEGMENTS_PER_ANTENNA = 1;

/**
 * Emit a single vertical line above a roof.
 *
 * @param {object} writer
 * @param {number} x
 * @param {number} z
 * @param {number} fromY
 * @param {number} toY
 * @param {{r: number, g: number, b: number}} color
 * @returns {number} Segments written.
 */
export function emitAntenna(writer, x, z, fromY, toY, color) {
  writer.write(x, fromY, z, x, toY, z, color, color);
  return SEGMENTS_PER_ANTENNA;
}

/** Crown edges radiating from the tip down to the base ring, and the ring's own sides. */
const CROWN_SPOKES = 4;

/**
 * How many line segments a tree occupies.
 *
 * Trunk, one spoke per crown corner, the base ring, and for taller trees a second ring higher up.
 *
 * @param {boolean} hasSecondTier
 * @returns {number}
 */
export function segmentsForTree(hasSecondTier) {
  return 1 + CROWN_SPOKES + CROWN_SPOKES + (hasSecondTier ? CROWN_SPOKES : 0);
}

/**
 * Emit a conifer.
 *
 * ## Why this silhouette
 *
 * A tip with spokes running down to a square base ring reads as a tree from any angle and from
 * directly above, which matters because the chase camera looks down. Ten segments is cheap
 * enough that a few hundred per tile costs less than the terrain they stand on.
 *
 * The second tier on taller trees exists purely so a stand does not look like one shape repeated
 * at different scales.
 *
 * @param {object} writer - From `createGeometryWriter`.
 * @param {object} tree
 * @param {number} tree.x
 * @param {number} tree.z
 * @param {number} tree.groundY - Ground height at the trunk.
 * @param {number} tree.height - Total height above the ground.
 * @param {number} tree.crownRadius
 * @param {number} tree.trunkHeight - Bare trunk below the crown.
 * @param {number|null} tree.secondTierY - Absolute height of the second ring, or null.
 * @param {number} tree.secondTierRadius
 * @param {{low: object, high: object}} gradient
 * @returns {number} Segments written.
 */
export function emitTree(writer, tree, gradient) {
  const baseY = tree.groundY;
  const crownBaseY = baseY + tree.trunkHeight;
  const tipY = baseY + tree.height;

  const trunkColor = gradient.low;
  const tipColor = gradient.high;
  const crownBaseColor = colorAtHeight(gradient, tree.trunkHeight / tree.height);

  // Trunk.
  writer.write(tree.x, baseY, tree.z, tree.x, crownBaseY, tree.z, trunkColor, crownBaseColor);

  /*
    Square base ring rather than a circle: four corners is the fewest that still reads as a
    volume from above, and a circle would cost several times as many segments for a shape that
    is only a few pixels across at any distance.
  */
  const corners = [
    [tree.x - tree.crownRadius, tree.z],
    [tree.x, tree.z - tree.crownRadius],
    [tree.x + tree.crownRadius, tree.z],
    [tree.x, tree.z + tree.crownRadius],
  ];

  let written = 1;

  for (let i = 0; i < corners.length; i += 1) {
    const [x, z] = corners[i];
    const [nextX, nextZ] = corners[(i + 1) % corners.length];

    // Spoke from the crown corner up to the tip.
    writer.write(x, crownBaseY, z, tree.x, tipY, tree.z, crownBaseColor, tipColor);
    // Ring edge around the base of the crown.
    writer.write(x, crownBaseY, z, nextX, crownBaseY, nextZ, crownBaseColor, crownBaseColor);
    written += 2;
  }

  if (tree.secondTierY !== null && tree.secondTierY !== undefined) {
    const tierColor = colorAtHeight(gradient, (tree.secondTierY - baseY) / tree.height);
    const tierCorners = [
      [tree.x - tree.secondTierRadius, tree.z],
      [tree.x, tree.z - tree.secondTierRadius],
      [tree.x + tree.secondTierRadius, tree.z],
      [tree.x, tree.z + tree.secondTierRadius],
    ];

    for (let i = 0; i < tierCorners.length; i += 1) {
      const [x, z] = tierCorners[i];
      const [nextX, nextZ] = tierCorners[(i + 1) % tierCorners.length];
      writer.write(x, tree.secondTierY, z, nextX, tree.secondTierY, nextZ, tierColor, tierColor);
      written += 1;
    }
  }

  return written;
}

// ---------------------------------------------------------------------------
// Depth mask geometry
// ---------------------------------------------------------------------------

/**
 * Vertices and triangles one box mask occupies.
 *
 * Four base corners plus four roof corners. Ten triangles: two per side and two for the roof.
 * No floor — nothing can ever see a building's underside, and skipping it saves a fifth of the
 * triangles for free.
 */
export const MASK_VERTICES_PER_BOX = 8;
export const MASK_TRIANGLES_PER_BOX = 10;

/** Indices per triangle. */
const INDICES_PER_TRIANGLE = 3;

/**
 * A cursor over an indexed triangle buffer pair.
 *
 * Indexed rather than expanded, because mask vertices are shared by several triangles each and
 * the mask carries no colour or normal to differentiate them.
 *
 * @param {Float32Array} positions - The window this writer fills, starting at its element 0.
 *   Pass a subarray to append after geometry that is already present.
 * @param {Uint32Array} indices - Likewise for indices.
 * @param {number} [baseVertex] - Index the first vertex should be *numbered* as.
 *
 *   Deliberately separate from where vertices are written. When appending after a terrain
 *   surface, the arrays are already offset to the free space, but the indices still have to
 *   count from the start of the whole buffer or the triangles reference terrain vertices and
 *   stretch across the entire tile.
 * @param {number} [originX] - Subtracted from every X written, so mask geometry lands in the same
 *   tile-relative space as the lines it occludes.
 * @param {number} [originZ]
 * @returns {object}
 */
export function createMaskWriter(positions, indices, baseVertex = 0, originX = 0, originZ = 0) {
  let vertexCount = 0;
  let indexCount = 0;

  return {
    /**
     * Append a vertex.
     *
     * @returns {number} Its index in the whole buffer, for passing to `addTriangle`.
     */
    addVertex(x, y, z) {
      const write = vertexCount * COMPONENTS_PER_VERTEX;
      if (write + COMPONENTS_PER_VERTEX > positions.length) {
        throw new Error('maskWriter: ran out of vertex room; the estimate is too low');
      }

      positions[write] = x - originX;
      positions[write + 1] = y;
      positions[write + 2] = z - originZ;

      const index = baseVertex + vertexCount;
      vertexCount += 1;
      return index;
    },

    /** Append a triangle by vertex index. */
    addTriangle(a, b, c) {
      if (indexCount + INDICES_PER_TRIANGLE > indices.length) {
        throw new Error('maskWriter: ran out of index room; the estimate is too low');
      }

      indices[indexCount] = a;
      indices[indexCount + 1] = b;
      indices[indexCount + 2] = c;
      indexCount += INDICES_PER_TRIANGLE;
    },

    getVertexCount() {
      return vertexCount;
    },

    getTriangleCount() {
      return indexCount / INDICES_PER_TRIANGLE;
    },
  };
}

/**
 * Profiles for standing rock, as radius against height fraction.
 *
 * A profile is a list of rings from the ground up: `at` is a fraction of total height, `radius` a multiple of the base
 * radius. Both the drawn lines and the depth mask come from the same list, so they cannot disagree.
 *
 * **The hoodoo's caprock is why these are structures and not terrain.** Its widest ring sits *above* a narrower shaft,
 * and a height field has one height per coordinate by definition — it cannot represent an overhang at any resolution.
 */
export const ROCK_PROFILES = {
  /** A tapering pinnacle. Monument Valley's totem poles. Never quite a point, so the tip stays a small ring. */
  spire: [
    { at: 0, radius: 1 },
    { at: 0.52, radius: 0.54 },
    { at: 1, radius: 0.12 },
  ],

  /**
   * A slender shaft carrying a caprock wider than itself. Bryce Canyon.
   *
   * The ring at 0.78 is *wider* than the two below it — that overhang is the whole silhouette, and it is the harder
   * stratum that protected the column while the ground around it eroded away.
   */
  hoodoo: [
    { at: 0, radius: 1 },
    { at: 0.42, radius: 0.66 },
    { at: 0.72, radius: 0.55 },
    { at: 0.78, radius: 1.05 },
    { at: 0.9, radius: 0.92 },
    { at: 1, radius: 0.42 },
  ],
};

/** The profile a column uses, falling back rather than throwing on an unknown name. */
function profileFor(name) {
  return ROCK_PROFILES[name] ?? ROCK_PROFILES.spire;
}

/**
 * The widest ring in a profile, as a multiple of base radius.
 *
 * **Not always 1.** A hoodoo's caprock is wider than its base, which is the whole shape — so placement has to reserve
 * space for the *caprock*, not the footing. Reserving only the base left two neighbouring caps able to approach closer
 * than intended, which would have quietly weakened the guarantee that columns never touch.
 *
 * @param {string} profileName
 * @returns {number}
 */
export function widestRadiusFor(profileName) {
  return profileFor(profileName).reduce((widest, ring) => Math.max(widest, ring.radius), 0);
}

/**
 * The ring stack for a rock column, ready for both line and mask emission.
 *
 * @param {object} column
 * @param {number} column.x
 * @param {number} column.z
 * @param {number} column.groundY
 * @param {number} column.height
 * @param {number} column.baseRadius
 * @param {number} column.sides
 * @param {number} column.spin - Rotation offset in turns, so a field is not a row of identical extrusions.
 * @param {string} column.profile - A key of `ROCK_PROFILES`.
 * @returns {{rings: Array<{points: Array<[number, number, number]>}>}}
 */
export function rockColumnSolid(column) {
  const rings = profileFor(column.profile).map(({ at, radius }) => {
    const y = column.groundY + column.height * at;
    const r = column.baseRadius * radius;
    const points = [];

    for (let i = 0; i < column.sides; i += 1) {
      const angle = ((i + (column.spin ?? 0)) / column.sides) * Math.PI * 2;
      points.push([column.x + Math.cos(angle) * r, y, column.z + Math.sin(angle) * r]);
    }

    return { points };
  });

  return { rings };
}

/**
 * Segments a stack of rings occupies: every ring closed as a polygon, plus the longitudinals joining
 * consecutive rings.
 *
 * Generic over the *path* the rings follow. A rock column stacks them vertically; a natural arch sweeps them
 * along a curve from one foot to the other. Neither the count nor the emitter below cares which.
 *
 * @param {number} rings
 * @param {number} sides
 * @returns {number}
 */
export function segmentsForRingStack(rings, sides) {
  return rings * sides + (rings - 1) * sides;
}

/** Mask vertices and triangles a ring stack occupies. Mirrors what `emitSolidMask` writes. */
export function maskForRingStack(rings, sides) {
  return {
    vertices: rings * sides + 1,
    triangles: (rings - 1) * sides * 2 + sides,
  };
}

/**
 * Emit a ring stack as lines: each ring closed, and each ring joined to the next.
 *
 * Colour runs dark at the bottom to bright at the top along the biome's own gradient, so the shape reads as lit
 * from above and picks up the same banding the terraced terrain has. Height fraction is measured against the
 * shape's own extent, which is why the caller passes it rather than this deriving it from the rings — an arch's
 * highest ring is its crown, not its last one.
 *
 * @param {object} writer - From `createGeometryWriter`.
 * @param {{rings: Array<{points: Array<[number, number, number]>}>}} solid
 * @param {{low: object, high: object}} gradient
 * @param {{groundY: number, height: number}} extent
 * @returns {number} Segments written.
 */
export function emitRingStack(writer, solid, gradient, { groundY, height }) {
  const colourAt = (y) => colorAtHeight(gradient, (y - groundY) / Math.max(1, height));

  let written = 0;

  for (const ring of solid.rings) {
    const { points } = ring;
    for (let i = 0; i < points.length; i += 1) {
      const [x, y, z] = points[i];
      const [nx, ny, nz] = points[(i + 1) % points.length];
      writer.write(x, y, z, nx, ny, nz, colourAt(y), colourAt(ny));
      written += 1;
    }
  }

  for (let r = 0; r < solid.rings.length - 1; r += 1) {
    const lower = solid.rings[r].points;
    const upper = solid.rings[r + 1].points;

    for (let i = 0; i < lower.length; i += 1) {
      const [x, y, z] = lower[i];
      const [ux, uy, uz] = upper[i];
      writer.write(x, y, z, ux, uy, uz, colourAt(y), colourAt(uy));
      written += 1;
    }
  }

  return written;
}

/** Segments a rock column occupies. */
export function segmentsForRockColumn(profileName, sides) {
  return segmentsForRingStack(profileFor(profileName).length, sides);
}

/** Mask vertices and triangles a rock column occupies. */
export function maskForRockColumn(profileName, sides) {
  return maskForRingStack(profileFor(profileName).length, sides);
}

/**
 * Emit a standing rock column — a spire or a hoodoo.
 *
 * @param {object} writer - From `createGeometryWriter`.
 * @param {object} column - Same shape `rockColumnSolid` takes.
 * @param {{low: object, high: object}} gradient
 * @returns {number} Segments written.
 */
export function emitRockColumn(writer, column, gradient) {
  return emitRingStack(writer, rockColumnSolid(column), gradient, column);
}

/**
 * Line segments a windpump occupies, given its blade count.
 *
 * Tower: four tapering legs, a ring at the top, a mid ring, and one diagonal per face on each of two tiers.
 * Wheel: a rim polygon plus one spoke per blade. Tail: three lines forming a vane.
 *
 * @param {number} blades
 * @returns {number}
 */
export function segmentsForWindpump(blades) {
  /*
    Mirrors `emitWindpump` term by term, and the two are pinned together by a test.

    The first version of this counted one segment per leg where the emitter writes two — a leg runs base to
    middle and middle to top — and the four-segment shortfall overran the tile buffer. Exactly the mismatch
    `segmentsForTree` needed a test for.
  */
  const TOWER_TIERS = 2;
  const TOWER_SIDES = 4;

  const legs = TOWER_SIDES * TOWER_TIERS;
  const rings = TOWER_TIERS * TOWER_SIDES;
  const braces = TOWER_TIERS * TOWER_SIDES;
  const rim = blades;
  const spokes = blades;
  const tail = 3;

  return legs + rings + braces + rim + spokes + tail;
}

/**
 * Emit a farm windpump: a lattice tower with a many-vaned fan wheel and a tail vane.
 *
 * The Kansas farmyard windmill, which is a water pump rather than a generator. Its silhouette is unlike
 * anything else in the world — a wide, densely spoked disc on a thin tapering tower — and that reads at
 * flight distance where finer detail would not.
 *
 * **It does not turn.** Motion is not a general tool in this project: the world is a pure function of
 * position and animating one thing invites animating everything. A static wheel still reads correctly.
 *
 * The tower is left unmasked. Like a transmission pylon, its wireframe *is* the structure — there is no
 * solid surface for a mask to represent, and masking an open frame would make it occlude like a slab.
 *
 * @param {object} writer - From `createGeometryWriter`.
 * @param {object} pump
 * @param {number} pump.x
 * @param {number} pump.z
 * @param {number} pump.groundY
 * @param {number} pump.height - Tower height, below the wheel.
 * @param {number} pump.baseWidth
 * @param {number} pump.wheelRadius
 * @param {number} pump.blades
 * @param {number} pump.facing - Radians. Which way the wheel points.
 * @param {{low: object, high: object}} gradient
 * @returns {number} Segments written.
 */
export function emitWindpump(writer, pump, gradient) {
  const baseY = pump.groundY;
  const topY = baseY + pump.height;
  const midY = baseY + pump.height * 0.5;

  const low = colorAtHeight(gradient, 0);
  const mid = colorAtHeight(gradient, 0.5);
  const high = colorAtHeight(gradient, 1);

  /** Tower footprint at a height fraction, tapering to a quarter width at the top. */
  const corners = (fraction) => {
    const half = (pump.baseWidth / 2) * (1 - 0.75 * fraction);
    return [
      [pump.x - half, pump.z - half],
      [pump.x + half, pump.z - half],
      [pump.x + half, pump.z + half],
      [pump.x - half, pump.z + half],
    ];
  };

  const base = corners(0);
  const middle = corners(0.5);
  const top = corners(1);

  let written = 0;

  // Legs, base to top through the mid ring.
  for (let i = 0; i < 4; i += 1) {
    writer.write(base[i][0], baseY, base[i][1], middle[i][0], midY, middle[i][1], low, mid);
    writer.write(middle[i][0], midY, middle[i][1], top[i][0], topY, top[i][1], mid, high);
    written += 2;
  }

  // Rings and one diagonal per face on each tier, which is what makes it read as braced.
  for (const [ring, y, colour, lower, lowerY, lowerColour] of [
    [middle, midY, mid, base, baseY, low],
    [top, topY, high, middle, midY, mid],
  ]) {
    for (let i = 0; i < 4; i += 1) {
      const next = (i + 1) % 4;
      writer.write(ring[i][0], y, ring[i][1], ring[next][0], y, ring[next][1], colour, colour);
      writer.write(lower[i][0], lowerY, lower[i][1], ring[next][0], y, ring[next][1], lowerColour, colour);
      written += 2;
    }
  }

  /*
    The wheel, in the vertical plane it faces. Its rim is a polygon and every blade is a spoke to the hub —
    a densely spoked disc, which is the whole silhouette.
  */
  const hubY = topY + pump.wheelRadius * 0.35;
  const acrossX = Math.cos(pump.facing);
  const acrossZ = Math.sin(pump.facing);

  const rimPoint = (index) => {
    const angle = (index / pump.blades) * Math.PI * 2;
    const across = Math.cos(angle) * pump.wheelRadius;
    const up = Math.sin(angle) * pump.wheelRadius;
    return [pump.x + acrossX * across, hubY + up, pump.z + acrossZ * across];
  };

  for (let i = 0; i < pump.blades; i += 1) {
    const [ax, ay, az] = rimPoint(i);
    const [bx, by, bz] = rimPoint(i + 1);

    writer.write(ax, ay, az, bx, by, bz, high, high);
    writer.write(pump.x, hubY, pump.z, ax, ay, az, mid, high);
    written += 2;
  }

  /*
    Tail vane, opposite the wheel. Small, and the reason a windpump reads as pointing somewhere rather than
    as a bare disc on a stick.
  */
  const tailLength = pump.wheelRadius * 1.5;
  const tailX = pump.x - acrossX * 0 - Math.sin(pump.facing) * 0;
  const backX = pump.x - Math.cos(pump.facing + Math.PI / 2) * tailLength;
  const backZ = pump.z - Math.sin(pump.facing + Math.PI / 2) * tailLength;
  const vaneTopY = hubY + pump.wheelRadius * 0.5;

  writer.write(tailX, hubY, pump.z, backX, hubY, backZ, mid, mid);
  writer.write(backX, hubY, backZ, backX, vaneTopY, backZ, mid, high);
  writer.write(backX, vaneTopY, backZ, tailX, hubY, pump.z, high, mid);
  written += 3;

  return written;
}

/**
 * Emit the solid form of a stack of horizontal rings, for depth masking only.
 *
 * Generic over every building archetype, because `buildingShapes.js` describes each one as ring stacks
 * and derives its *lines* from the same rings. That is what guarantees a mask can never disagree with
 * the shape it masks — a spire's mask tapers because the spire's rings taper, and no per-archetype mask
 * code exists to fall out of step.
 *
 * The lateral surface is a band of quads between each pair of consecutive rings. The top is closed with
 * a fan from an added centre vertex, or from the apex when the shape has one.
 *
 * No floor, for the same reason trees have none: the flight model never lets the camera under a
 * building's footprint, and the sides already close the shape from every angle that can be reached.
 *
 * Counts must match `buildingShapes.countMask`, which is derived from the same ring arithmetic.
 *
 * @param {object} writer - From `createMaskWriter`.
 * @param {{rings: Array<{points: Array<[number, number, number]>}>, apex?: [number, number, number]}} solid
 * @returns {{vertices: number, triangles: number}} What was written.
 */
export function emitSolidMask(writer, solid) {
  const ringIndices = [];

  for (const ring of solid.rings) {
    ringIndices.push(ring.points.map(([x, y, z]) => writer.addVertex(x, y, z)));
  }

  let triangles = 0;

  // Lateral bands.
  for (let r = 0; r < ringIndices.length - 1; r += 1) {
    const lower = ringIndices[r];
    const upper = ringIndices[r + 1];

    for (let i = 0; i < lower.length; i += 1) {
      const next = (i + 1) % lower.length;

      /*
        Two triangles per quad. Winding is not load-bearing: the mask material writes depth only and
        never colour, so back-face culling is off and a reversed quad still occludes correctly.
      */
      writer.addTriangle(lower[i], upper[i], upper[next]);
      writer.addTriangle(lower[i], upper[next], lower[next]);
      triangles += 2;
    }
  }

  // Cap.
  const top = solid.rings.at(-1);
  const topIndices = ringIndices.at(-1);

  let capIndex;
  if (solid.apex) {
    capIndex = writer.addVertex(solid.apex[0], solid.apex[1], solid.apex[2]);
  } else {
    /*
      The ring's centroid in all three axes, not just in the horizontal plane.

      Originally this took its height from the ring's first point, which is exact for a *horizontal* ring and so was
      correct for every building, rock column and arch — all of their capped rings are level. A telescope dish is
      the first shape here whose last ring is tilted, and reading one vertex's height would have put the cap centre
      up to a rim's worth of height out of place, leaving a depth mask that occludes the wrong part of the screen.
      Averaging is identical for a level ring, so nothing that already worked changes.
    */
    let sumX = 0;
    let sumY = 0;
    let sumZ = 0;
    for (const [x, y, z] of top.points) {
      sumX += x;
      sumY += y;
      sumZ += z;
    }
    const count = top.points.length;
    capIndex = writer.addVertex(sumX / count, sumY / count, sumZ / count);
  }

  for (let i = 0; i < topIndices.length; i += 1) {
    writer.addTriangle(topIndices[i], topIndices[(i + 1) % topIndices.length], capIndex);
    triangles += 1;
  }

  return { vertices: ringIndices.flat().length + 1, triangles };
}

/**
 * Vertices and triangles one tree mask occupies: four crown corners plus the tip.
 *
 * No floor. Looking up into a tree from underneath is not a view the flight model permits, and the
 * four sides already close the shape from every angle that matters.
 */
export const MASK_VERTICES_PER_TREE = 5;
export const MASK_TRIANGLES_PER_TREE = 4;

/**
 * Emit the solid form of a tree's crown, for depth masking only.
 *
 * ## Why a pyramid is exactly the right shape
 *
 * The four crown spokes `emitTree` draws *are* this pyramid's edges, so the mask covers precisely
 * the silhouette the tree covers — no more. That matters: the first instinct was to leave trees
 * unmasked on the grounds that a solid hull would occlude more than a spiky shape should. It does
 * not, because the hull and the drawn silhouette are the same thing.
 *
 * What trees actually needed masking for only shows up from above. Looking down through a stand
 * puts dozens of crowns along the same line of sight, and unmasked they pile into a flat mass of
 * green with no individual trees legible. Looking *across* a stand, which is what the earlier
 * reasoning imagined, they are sparse enough not to stack.
 *
 * The trunk is left unmasked. It is a single line, and masking it would occlude thin slivers of
 * whatever is behind for no visible gain.
 *
 * @param {object} writer - From `createMaskWriter`.
 * @param {object} tree - Same shape `emitTree` takes.
 * @returns {number} Triangles written.
 */
export function emitTreeMask(writer, tree) {
  const crownBaseY = tree.groundY + tree.trunkHeight;
  const tipY = tree.groundY + tree.height;

  // Same four corners, in the same order, as the crown ring in `emitTree`.
  const corners = [
    [tree.x - tree.crownRadius, tree.z],
    [tree.x, tree.z - tree.crownRadius],
    [tree.x + tree.crownRadius, tree.z],
    [tree.x, tree.z + tree.crownRadius],
  ];

  const base = corners.map(([x, z]) => writer.addVertex(x, crownBaseY, z));
  const tip = writer.addVertex(tree.x, tipY, tree.z);

  for (let i = 0; i < base.length; i += 1) {
    writer.addTriangle(base[i], base[(i + 1) % base.length], tip);
  }

  return MASK_TRIANGLES_PER_TREE;
}

/**
 * Emit the solid form of a tower, for depth masking only.
 *
 * Matches the box `emitBox` draws, so the wireframe edges sit exactly on the mask's edges and
 * a depth bias is what keeps them visible. The base follows the ground at each corner for the
 * same reason the wireframe does: a mask that ignored the slope would either float and let
 * lines show underneath, or sink and clip the base edges away.
 *
 * @param {object} writer - From `createMaskWriter`.
 * @param {object} box - Same shape `emitBox` takes.
 * @returns {number} Triangles written.
 */
export function emitBoxMask(writer, box) {
  const halfWidth = box.width / 2;
  const halfDepth = box.depth / 2;

  const corners = [
    [box.centerX - halfWidth, box.centerZ - halfDepth],
    [box.centerX + halfWidth, box.centerZ - halfDepth],
    [box.centerX + halfWidth, box.centerZ + halfDepth],
    [box.centerX - halfWidth, box.centerZ + halfDepth],
  ];

  const base = [];
  const roof = [];

  for (let i = 0; i < corners.length; i += 1) {
    const [x, z] = corners[i];
    base.push(writer.addVertex(x, box.cornerGroundY[i], z));
    roof.push(writer.addVertex(x, box.roofY, z));
  }

  // Four walls.
  for (let i = 0; i < corners.length; i += 1) {
    const next = (i + 1) % corners.length;
    writer.addTriangle(base[i], roof[i], base[next]);
    writer.addTriangle(base[next], roof[i], roof[next]);
  }

  // Roof.
  writer.addTriangle(roof[0], roof[1], roof[2]);
  writer.addTriangle(roof[0], roof[2], roof[3]);

  return MASK_TRIANGLES_PER_BOX;
}

/**
 * Colour at a fraction of the way up a structure.
 *
 * Exposed so callers can colour their own primitives consistently with `emitBox`.
 *
 * @param {{low: object, high: object}} gradient
 * @param {number} heightFraction - 0 at the base, 1 at the roof. Clamped.
 * @returns {{r: number, g: number, b: number}}
 */
export function colorAtHeight(gradient, heightFraction) {
  return mix(gradient.low, gradient.high, heightFraction);
}

/**
 * Segments one wind turbine costs.
 *
 * Mirrors `emitWindTurbine` term by term, and the two are pinned together by a test — the buffer is sized before
 * anything is written, so the count cannot come from the writing.
 *
 * @param {{sides: number, rings: number, blades: number, chords: number}} shape
 * @returns {number}
 */
export function segmentsForWindTurbine({ sides, rings, blades, chords }) {
  /*
    Tower. **One vertical per side per ring, not one per side** — the verticals are broken at every ring so the
    taper is drawn as a chain rather than a straight line from base to hub. Getting this wrong undersized the
    tile's shared buffer, and the emitter that happened to run afterwards was the one that threw.

    Plus a closed ring at every level above the ground, which is the same count again.
  */
  const tower = sides * rings * 2;

  // Nacelle: a box, so two rings of four and four verticals joining them.
  const nacelle = 12;

  // Each blade is two converging edges plus its chords.
  const rotor = blades * (2 + chords);

  return tower + nacelle + rotor;
}

/**
 * Emit a large wind turbine: a tapered tower, a nacelle, and a three-bladed rotor.
 *
 * ## What makes it read as a turbine rather than a tower with a stick on it
 *
 * The blades. Drawn as single lines they read as a radio mast's guy wires, so each one is **two converging
 * edges with chords across them** — a long thin triangle, which is what an aerofoil looks like in silhouette.
 * That is three quarters of the segments here and all of the recognition.
 *
 * The tower is deliberately cheap by comparison. It is slender enough that only its two silhouette edges
 * actually read at distance, so the rings exist to give it scale against an empty sky rather than to describe a
 * cylinder.
 *
 * ## Static, and the rotor angle is per turbine
 *
 * Nothing moves in this world: it is a pure function of position, and animating one thing invites animating
 * everything. What replaces motion is that **every turbine in a row sits at a different rotor angle**, which is
 * what a photograph of a wind farm looks like anyway, and costs a rotation rather than a frame of work.
 *
 * @param {object} writer - From `createGeometryWriter`.
 * @param {object} turbine - From `windFarm.js`.
 * @param {{low: object, high: object}} gradient
 * @returns {number} Segments written.
 */
export function emitWindTurbine(writer, turbine, gradient) {
  const baseY = turbine.groundY;
  const hubY = baseY + turbine.hubHeight;
  const height = Math.max(1, turbine.hubHeight + turbine.bladeLength);

  /**
   * Colour at an absolute height, over the whole turbine including the blades.
   *
   * Measured over the *tip* height rather than the hub, so the topmost blade is the brightest thing on the
   * machine. Using the hub would clip every blade above it to the same end of the gradient and lose the rotor
   * against the sky it is drawn on.
   */
  const colourAt = (y) =>
    colorAtHeight(gradient, Math.min(1, Math.max(0, (y - baseY) / height)));

  let written = 0;

  // --- tower ---------------------------------------------------------------

  /*
    Dimensions travel on the structure rather than being read from config, which is the rule this whole module
    follows — it has no imports at all, so it can be reasoned about and tested without the world's tuning. The
    windpump does the same with its wheel radius and blade count.
  */
  const sides = turbine.towerSides;
  const rings = turbine.towerRings;

  /** A ring of corners at a height fraction up the tower, tapering as it rises. */
  const ringAtFraction = (fraction) => {
    const radius = turbine.towerBaseRadius * (1 - fraction * (1 - turbine.towerTopRatio));
    const y = baseY + turbine.hubHeight * fraction;
    const corners = [];

    for (let i = 0; i < sides; i += 1) {
      const angle = (i / sides) * Math.PI * 2;
      corners.push([turbine.x + Math.cos(angle) * radius, y, turbine.z + Math.sin(angle) * radius]);
    }

    return corners;
  };

  const levels = [];
  for (let r = 0; r <= rings; r += 1) levels.push(ringAtFraction(r / rings));

  // Verticals, bottom to top through every ring.
  for (let i = 0; i < sides; i += 1) {
    for (let r = 0; r < levels.length - 1; r += 1) {
      const a = levels[r][i];
      const b = levels[r + 1][i];
      writer.write(a[0], a[1], a[2], b[0], b[1], b[2], colourAt(a[1]), colourAt(b[1]));
    }
    written += levels.length - 1;
  }

  /*
    Rings at every level except the ground, where the tower meets the earth and a closed loop would only draw a
    bright ellipse on the grass.
  */
  for (let r = 1; r < levels.length; r += 1) {
    const ring = levels[r];
    const colour = colourAt(ring[0][1]);

    for (let i = 0; i < sides; i += 1) {
      const a = ring[i];
      const b = ring[(i + 1) % sides];
      writer.write(a[0], a[1], a[2], b[0], b[1], b[2], colour, colour);
    }
    written += sides;
  }

  // --- nacelle -------------------------------------------------------------

  /*
    The rotor axis and the two directions square to it. The rotor plane is spanned by `across` and the vertical,
    which is what every blade is laid out in.
  */
  const axisX = Math.cos(turbine.facing);
  const axisZ = Math.sin(turbine.facing);
  const acrossX = -axisZ;
  const acrossZ = axisX;

  const halfLength = turbine.nacelleLength / 2;
  const halfWidth = turbine.nacelleWidth / 2;
  const nacelleTop = hubY + turbine.nacelleHeight / 2;
  const nacelleBottom = hubY - turbine.nacelleHeight / 2;

  /** A corner of the nacelle box, in its own frame. */
  const nacelleCorner = (along, across, y) => [
    turbine.x + axisX * along + acrossX * across,
    y,
    turbine.z + axisZ * along + acrossZ * across,
  ];

  const nacelleRing = (y) => [
    nacelleCorner(-halfLength, -halfWidth, y),
    nacelleCorner(halfLength, -halfWidth, y),
    nacelleCorner(halfLength, halfWidth, y),
    nacelleCorner(-halfLength, halfWidth, y),
  ];

  const nacelleLower = nacelleRing(nacelleBottom);
  const nacelleUpper = nacelleRing(nacelleTop);
  const nacelleColour = colourAt(hubY);

  for (let i = 0; i < 4; i += 1) {
    const next = (i + 1) % 4;

    writer.write(...nacelleLower[i], ...nacelleLower[next], nacelleColour, nacelleColour);
    writer.write(...nacelleUpper[i], ...nacelleUpper[next], nacelleColour, nacelleColour);
    writer.write(...nacelleLower[i], ...nacelleUpper[i], nacelleColour, nacelleColour);
    written += 3;
  }

  // --- rotor ---------------------------------------------------------------

  /*
    The hub sits at the upwind end of the nacelle, which is what stops the blades sprouting from the middle of
    the machine.
  */
  const hubX = turbine.x + axisX * halfLength;
  const hubZ = turbine.z + axisZ * halfLength;

  /** A point in the rotor plane, at a distance from the hub along a blade and an offset across it. */
  const rotorPoint = (radius, offset, angle) => {
    const alongUp = Math.cos(angle);
    const alongAcross = Math.sin(angle);

    // Square to the blade, still in the rotor plane.
    const sideUp = -alongAcross;
    const sideAcross = alongUp;

    const across = alongAcross * radius + sideAcross * offset;
    const up = alongUp * radius + sideUp * offset;

    return [hubX + acrossX * across, hubY + up, hubZ + acrossZ * across];
  };

  for (let blade = 0; blade < turbine.blades; blade += 1) {
    const angle = turbine.phase + (blade / turbine.blades) * Math.PI * 2;

    const halfRoot = turbine.bladeRootWidth / 2;
    const tip = rotorPoint(turbine.bladeLength, 0, angle);

    /*
      Two edges from the root out to a single tip. Tapering to a point rather than to a narrow chord because a
      blade genuinely does, and because a point costs one fewer segment.
    */
    for (const side of [-1, 1]) {
      const root = rotorPoint(turbine.hubRadius, side * halfRoot, angle);
      writer.write(...root, ...tip, colourAt(root[1]), colourAt(tip[1]));
    }
    written += 2;

    /*
      Chords across the blade, narrowing as they go out. Without them the two edges read as a V; with them the
      blade reads as a surface, which is the difference between a turbine and an aerial.
    */
    for (let c = 0; c < turbine.bladeChords; c += 1) {
      const fraction = (c + 1) / (turbine.bladeChords + 1);
      const radius = turbine.hubRadius + (turbine.bladeLength - turbine.hubRadius) * fraction;
      const width = halfRoot * (1 - fraction);

      const a = rotorPoint(radius, -width, angle);
      const b = rotorPoint(radius, width, angle);
      writer.write(...a, ...b, colourAt(a[1]), colourAt(b[1]));
    }
    written += turbine.bladeChords;
  }

  return written;
}
